import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

// Shared UI Library Components
import { CustomButton, CustomIcon, EIconName } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './stripe-account.scss';

// Store entities
import { getCompanyState } from '../../../../../../store/company/company.selectors';
import { connectStripeToCompany, fetchStipeAccountLink } from '../../../../../../store/company/company.thunks';

type TStripeAccountProps = {
  stripeAccountHash: string | null;
};

export const StripeAccount: FC<TStripeAccountProps> = (
  { stripeAccountHash = null }: TStripeAccountProps
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [connectingAccount, setConnectingAccount] = useState<boolean>(false);

  const { company } = useSelector(getCompanyState);

  const isAvailableStripe = !!company?.is_stripe_connected;

  const dispatch = useDispatch<any>();

  const generateStripeLink = async (): Promise<void> => {
    setConnectingAccount(true);
    const { payload: accountLink } = await dispatch(fetchStipeAccountLink({
      refresh_url: window.origin + '/business-profile/payment-details',
      return_url: window.origin + '/business-profile/payment-details',
    }));

    if (!accountLink) {
      setConnectingAccount(false);
      return;
    }
    window.location.assign(accountLink);
  }

  const connectStripeAccount = async (): Promise<void> => {
    if (!stripeAccountHash) return;
    setConnectingAccount(true);
    setSearchParams('');

    await dispatch(connectStripeToCompany(stripeAccountHash));
    setConnectingAccount(false);
  }

  useEffect(() => {
    connectStripeAccount();
  }, [stripeAccountHash]);

  return (<>
    <div className="stripe-account">
      <div className="stripe-account--label business--label">
        <div className="label-title">
          Stripe Account
        </div>
        <div className="label-actions">
          <CustomButton text={ isAvailableStripe ? 'Change Stripe Account' : 'Connect Stripe Account' }
                        type={ 'text' }
                        size={ 'small' }
                        onClick={ generateStripeLink } loading={ connectingAccount }/>
        </div>
      </div>
      <div className="stripe-account--description business--description">
        Accept payments from clients securely and easily! Connect your Stripe account to get started
      </div>
      <div className="stripe-account--body">
        <div className="body-item body-item--icon">
          <CustomIcon name={ EIconName.Stripe }/>
        </div>
        <div className="body-item body-item--name">
          Stripe Account
        </div>
        <div
          className={ `body-item body-item--status body-item--status-${ isAvailableStripe ? 'connected' : 'not-connected' }` }>
          { isAvailableStripe ? 'Connected' : 'Not connected' }
        </div>
      </div>
    </div>
  </>);
}
